import { publicEndpoint } from './publicContactApi';
import type { StateData } from './referenceDataApi';

/**
 * Send my Business booking — `api/public/send-my-business`. Anonymous.
 *
 * Booking sends the order (and the artwork file, if any) and gets back a Stripe Checkout URL for
 * the $440 professional fee. Nothing monthly is charged then: an admin starts the subscription
 * when the packs are printed. See SendMyBusinessBillingService.cs.
 */

/** Mirrors SendMyBusinessOrder.cs. */
export interface SendMyBusinessOrder {
    councilAreas: { state: string; name: string }[];
    termMonths: number;
    messageChoice: 'upload' | 'design';
    designDetails?: string;
    name: string;
    business?: string;
    phone?: string;
    email: string;
    samplesAddress: { street: string; suburb: string; state: string; postcode: string };
    acceptedTerms: boolean;
    authorisedBilling: boolean;
    termsVersion: string;
}

export interface SendMyBusinessConfig {
    checkoutEnabled: boolean;
    termsVersion: string;
}

export class BookingError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'BookingError';
    }
}

async function serverError(response: Response, fallback: string): Promise<BookingError> {
    if (response.status === 429) return new BookingError('That was a few tries in a short time. Please wait a minute and try again.');
    try {
        const body = await response.json();
        if (typeof body?.error === 'string') return new BookingError(body.error);
    } catch {
        // Not JSON — a proxy page. The fallback says more than its markup would.
    }
    return new BookingError(fallback);
}

export const sendMyBusinessApi = {
    /** Null when the server can't be reached: the page then behaves as if checkout is off. */
    async config(): Promise<SendMyBusinessConfig | null> {
        try {
            const response = await fetch(publicEndpoint('send-my-business/config'));
            return response.ok ? ((await response.json()) as SendMyBusinessConfig) : null;
        } catch {
            return null;
        }
    },

    /**
     * The councils a campaign can be booked for, by state — not reference data's list, which only
     * has the councils DA Intelligence sells. Includes NSW when the server offers it.
     */
    async councils(): Promise<StateData[]> {
        const response = await fetch(publicEndpoint('send-my-business/councils'));
        if (!response.ok) throw new BookingError("The list of councils couldn't be loaded.");
        return (await response.json()) as StateData[];
    },

    /** Returns the Stripe Checkout URL to send the customer to. */
    async book(order: SendMyBusinessOrder, artwork: File | null): Promise<string> {
        const form = new FormData();
        form.append('order', JSON.stringify(order));
        if (artwork) form.append('artwork', artwork);

        let response: Response;
        try {
            response = await fetch(publicEndpoint('send-my-business/bookings'), { method: 'POST', body: form });
        } catch {
            throw new BookingError("We couldn't reach us just then. Check your connection and try again.");
        }
        if (!response.ok) throw await serverError(response, "We couldn't start your booking just then. Please try again.");
        const body = (await response.json()) as { url: string };
        return body.url;
    },

    /** The booking's billing status after returning from Stripe; null if it can't be found. */
    async status(sessionId: string): Promise<string | null> {
        try {
            const response = await fetch(publicEndpoint(`send-my-business/bookings/status?session_id=${encodeURIComponent(sessionId)}`));
            if (!response.ok) return null;
            return ((await response.json()) as { status: string }).status;
        } catch {
            return null;
        }
    },
};
